import { useState, useEffect, useRef } from 'react'

// Cache permissions globally to avoid re-fetching
let permissionsCache = null
let cacheTimestamp = null
const CACHE_DURATION = 5 * 60 * 1000 // 5 minutes

// FLIGHT-570 bug 3: when a user switches portals (e.g. donor -> admin), the
// global cache gets cleared but already-mounted usePermissions instances kept
// their stale component-level state, so admin sidebar items would render
// against the previous portal's permissions and show as missing. The version
// counter bumps on every cache clear and is part of each hook's useEffect
// dependency list, forcing a re-fetch.
let cacheVersion = 0
const cacheVersionListeners = new Set()

function bumpCacheVersion() {
  cacheVersion += 1
  cacheVersionListeners.forEach((fn) => {
    try {
      fn(cacheVersion)
    } catch (_) {
      // ignore listener errors so one bad subscriber cannot freeze the rest
    }
  })
}

/**
 * Hook to check if current user has any of the specified permissions
 * @param {string[]} requiredPermissions - Array of permission names to check (optional)
 * @returns {object} - { hasPermission: boolean, loading: boolean, hasAnyPermission: function }
 */
function initFromCache(requiredPermissions) {
  const now = Date.now()
  if (permissionsCache && cacheTimestamp && now - cacheTimestamp < CACHE_DURATION) {
    const hasAny =
      !requiredPermissions?.length ||
      requiredPermissions.some((p) => permissionsCache.includes(p) || permissionsCache.includes('system:admin'))
    return { permissions: permissionsCache, hasPermission: hasAny, loading: false }
  }
  return { permissions: [], hasPermission: false, loading: true }
}

export function usePermissions(requiredPermissions) {
  const initial = initFromCache(requiredPermissions)
  const [hasPermission, setHasPermission] = useState(initial.hasPermission)
  const [loading, setLoading] = useState(initial.loading)
  const [userPermissions, setUserPermissions] = useState(initial.permissions)
  const [version, setVersion] = useState(cacheVersion)
  const isMountedRef = useRef(true)

  useEffect(() => {
    isMountedRef.current = true
    return () => {
      isMountedRef.current = false
    }
  }, [])

  // Subscribe to cache-version bumps so a portal switch forces a re-fetch.
  useEffect(() => {
    const onBump = (v) => setVersion(v)
    cacheVersionListeners.add(onBump)
    return () => cacheVersionListeners.delete(onBump)
  }, [])

  useEffect(() => {
    async function checkPermissions() {
      try {
        // Get auth state from localStorage
        const authState = JSON.parse(localStorage.getItem('givewise_auth_state') || '{}')
        const token = authState?.state?.rawToken

        if (!token) {
          if (isMountedRef.current) {
            setHasPermission(false)
            setLoading(false)
          }
          return
        }

        // Check if we have valid cached permissions
        const now = Date.now()
        if (permissionsCache && cacheTimestamp && now - cacheTimestamp < CACHE_DURATION) {
          if (isMountedRef.current) {
            setUserPermissions(permissionsCache)

            if (requiredPermissions && requiredPermissions.length > 0) {
              const hasAnyPermission = requiredPermissions.some(
                (perm) => permissionsCache.includes(perm) || permissionsCache.includes('system:admin')
              )
              setHasPermission(hasAnyPermission)
            }
            setLoading(false)
          }
          return
        }

        // Fetch user's permissions from API
        const graphqlUrl = `${process.env.REACT_APP_API_HOST || ''}${process.env.REACT_APP_GRAPHQL_PATH || '/graphql'}`
        const response = await fetch(graphqlUrl, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`,
          },
          credentials: 'include',
          body: JSON.stringify({
            query: `
              query {
                me {
                  id
                  permissions {
                    name
                  }
                }
              }
            `,
          }),
        })

        const result = await response.json()

        if (result.data?.me?.permissions) {
          const fetchedPermissions = result.data.me.permissions.map((p) => p.name)

          // Cache the permissions
          permissionsCache = fetchedPermissions
          cacheTimestamp = Date.now()

          if (isMountedRef.current) {
            setUserPermissions(fetchedPermissions)

            if (requiredPermissions && requiredPermissions.length > 0) {
              // Check if user has any of the required permissions
              const hasAnyPermission = requiredPermissions.some(
                (perm) => fetchedPermissions.includes(perm) || fetchedPermissions.includes('system:admin')
              )
              setHasPermission(hasAnyPermission)
            }
            setLoading(false)
          }
        } else {
          console.error('[usePermissions] No permissions data in response:', result)
          if (isMountedRef.current) {
            setHasPermission(false)
            setLoading(false)
          }
        }
      } catch (error) {
        console.error('[usePermissions] Error checking permissions:', error)
        if (isMountedRef.current) {
          setHasPermission(false)
          setLoading(false)
        }
      }
    }

    if (requiredPermissions && requiredPermissions.length > 0) {
      checkPermissions()
    } else {
      // No permissions required, fetch permissions anyway for hasAnyPermission function
      checkPermissions()
      setHasPermission(true)
    }
    // version is in deps so a portal switch (which bumps the version)
    // forces a re-fetch even if requiredPermissions did not change.
  }, [requiredPermissions ? requiredPermissions.join(',') : 'none', version])

  // Function to check if user has any of the given permissions
  const hasAnyPermission = (permsToCheck) => {
    if (!permsToCheck || permsToCheck.length === 0) return true
    return permsToCheck.some((perm) => userPermissions.includes(perm) || userPermissions.includes('system:admin'))
  }

  return { hasPermission, loading, hasAnyPermission, permissions: userPermissions }
}

// Clear cache and force all mounted hooks to re-fetch (use for impersonation/token change).
export function clearPermissionsCache() {
  permissionsCache = null
  cacheTimestamp = null
  bumpCacheVersion()
}

// Wipe cache without bumping the version (use for logout — components are unmounting
// anyway, so triggering re-fetches with a stale token would repopulate the cache with
// the wrong user's permissions before the next user logs in).
export function wipePermissionsCache() {
  permissionsCache = null
  cacheTimestamp = null
}
